import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, InputWrapper, SingleSelectInput } from "@performyard/py-components";

type CompetencyLite = {
	_id: string;
	name: string;
	levels: Array<{ rank: number; name: string }>;
};

export type AddCompetencyPickerProps = {
	availableCompetencies: CompetencyLite[];
	onAdd: (entry: { competency_id: string; expected_level: number | null }) => void;
	disabled?: boolean;
};

export const AddCompetencyPicker: React.FC<AddCompetencyPickerProps> = ({ availableCompetencies, onAdd, disabled }) => {
	const [selectedCompetencyId, setSelectedCompetencyId] = useState<string | null>(null);
	const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
	const [showCompetencyError, setShowCompetencyError] = useState(false);

	const competencyOptions = availableCompetencies.map((c) => ({ value: c._id, label: c.name }));
	const selectedCompetency = availableCompetencies.find((c) => c._id === selectedCompetencyId);
	const levelOptions =
		selectedCompetency?.levels.map((lvl) => ({ value: lvl.rank, label: `${lvl.name} (${lvl.rank})` })) ?? [];
	const hasLevels = levelOptions.length > 0;

	const handleCompetencyChange = (next: string | null | undefined) => {
		setSelectedCompetencyId(next ?? null);
		setSelectedLevel(null);
		if (next) setShowCompetencyError(false);
	};

	const handleAdd = () => {
		if (!selectedCompetencyId) {
			setShowCompetencyError(true);
			return;
		}
		onAdd({ competency_id: selectedCompetencyId, expected_level: selectedLevel });
		setSelectedCompetencyId(null);
		setSelectedLevel(null);
		setShowCompetencyError(false);
	};

	return (
		// Fixed-width competency column + a flexible level column, per Figma. The footer's
		// Cancel/Create Role buttons below no longer share a column grid with this row (out of
		// scope for this pass — see Phase 2.2 report).
		<div className="flex items-start gap-2" data-testid="competency-picker">
			<div className="w-[252px] shrink-0">
				<InputWrapper
					id="picker-competency"
					label="Competency"
					error={showCompetencyError ? "Please select a competency" : undefined}
				>
					<SingleSelectInput<string>
						id="picker-competency"
						value={selectedCompetencyId ?? undefined}
						placeholder="Select a competency"
						options={competencyOptions}
						onChange={handleCompetencyChange}
						disabled={disabled || competencyOptions.length === 0}
						errored={showCompetencyError}
						selectionVariant="combobox"
					/>
				</InputWrapper>
			</div>
			<div className="min-w-0 flex-1">
				<InputWrapper id="picker-level" label="Expected Level">
					<SingleSelectInput<number>
						id="picker-level"
						value={selectedLevel ?? undefined}
						placeholder={
							!selectedCompetency
								? "Make a selection"
								: hasLevels
									? "Make a selection"
									: "No levels created"
						}
						options={levelOptions}
						onChange={(v) => setSelectedLevel(v ?? null)}
						disabled={!selectedCompetency || levelOptions.length === 0}
						isClearable
					/>
				</InputWrapper>
			</div>
			{/* Invisible label spacer matches InputWrapper's label+gap height so the Add button
			    aligns vertically with the input row. */}
			<div className="flex flex-col gap-2">
				<span aria-hidden className="invisible text-paragraph-3">
					&nbsp;
				</span>
				<ButtonStandard
					variant="solid"
					color="primary"
					size="medium"
					iconLeft={faPlus}
					disabled={disabled}
					onClick={handleAdd}
					data-testid="add-competency-button"
				>
					Add
				</ButtonStandard>
			</div>
		</div>
	);
};
